import React from 'react';
import { Card, Dropdown, Tag, Button, Modal } from 'antd';
import type { MenuProps } from 'antd';
import {
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import type { Organization } from '../services/adminService';

/**
 * OrganizationListCard
 * ----------------------------------------------------------------------------
 * Renders a single Organization as a card for narrow viewports.
 *
 * Platform orgs cannot be deleted — the Delete menu item is hidden for them,
 * matching the desktop table's conditional Popconfirm rendering.
 */

const typeColor = (type: string): string => {
  switch (type) {
    case 'platform':
      return 'purple';
    case 'reseller':
      return 'blue';
    case 'customer':
      return 'green';
    default:
      return 'default';
  }
};

export type OrganizationListCardHandlers = {
  onEdit: (org: Organization) => void;
  onDelete: (id: number) => void;
};

type Props = {
  org: Organization;
  /** All orgs — needed to resolve parent_org_id to a name */
  allOrgs: Organization[];
  handlers: OrganizationListCardHandlers;
};

const FieldRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ display: 'flex', gap: 8, padding: '4px 0', fontSize: 13 }}>
    <span style={{ color: 'rgba(0,0,0,0.55)', minWidth: 80, flexShrink: 0 }}>{label}</span>
    <span style={{ minWidth: 0, wordBreak: 'break-word' }}>{children}</span>
  </div>
);

const OrganizationListCard: React.FC<Props> = ({ org, allOrgs, handlers }) => {
  const parentOrg = allOrgs.find(o => o.id === org.parent_org_id);

  const menuItems: MenuProps['items'] = [
    {
      key: 'edit',
      label: 'Edit',
      icon: <EditOutlined />,
      onClick: () => handlers.onEdit(org),
    },
    // Platform orgs cannot be deleted — hide the item entirely, matching the
    // desktop table's conditional Popconfirm.
    ...(org.type !== 'platform'
      ? [
          {
            key: 'delete',
            label: 'Delete',
            icon: <DeleteOutlined />,
            danger: true,
            onClick: () =>
              Modal.confirm({
                title: 'Delete organization',
                icon: <ExclamationCircleOutlined />,
                content: 'Are you sure you want to delete this organization?',
                okText: 'Yes, delete',
                okButtonProps: { danger: true },
                cancelText: 'Cancel',
                onOk: () => handlers.onDelete(org.id),
              }),
          },
        ]
      : []),
  ];

  return (
    <Card size="small" style={{ marginBottom: 8 }} styles={{ body: { padding: 12 } }}>
      {/* Top row: name (clickable) + kebab */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
        <div style={{ flex: 1, minWidth: 0, fontWeight: 500, wordBreak: 'break-word' }}>
          <Link to={`/admin/organizations/${org.id}`}>{org.name}</Link>
        </div>
        <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
          <Button
            type="text"
            icon={<MoreOutlined style={{ fontSize: 18 }} />}
            aria-label={`Actions for ${org.name}`}
            style={{ flexShrink: 0 }}
          />
        </Dropdown>
      </div>

      <FieldRow label="Type">
        <Tag color={typeColor(org.type)} style={{ margin: 0 }}>
          {org.type}
        </Tag>
      </FieldRow>
      <FieldRow label="Parent">{parentOrg ? parentOrg.name : '\u2014'}</FieldRow>
      <FieldRow label="Status">
        <Tag color={org.is_active ? 'green' : 'red'} style={{ margin: 0 }}>
          {org.is_active ? 'Active' : 'Inactive'}
        </Tag>
      </FieldRow>
    </Card>
  );
};

export default OrganizationListCard;
